import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import { closeDialog } from '../../../store/modules/dialog';
import Dialog from '../Dialog';
import { useTranslation } from 'react-i18next';
import GridLayout from '../../common/GridLayout';
import { useState, useRef, useCallback } from 'react';
import { insertActiveLink } from '../../plugin/activeLink';
import { usePOWebofficeSDKState } from '../../../context/poWebOfficeSDKContext';
import { CustomScrollbar, noTextSelect } from '../../common/CssCommon';
import { useGenNotificationPromise } from '../../notification/Notification';
import ActiveLinkDialogFooter from './ActiveLinkDialogFooter';
import iconTokTok from '../../../img/activeLink/ribbonIcon/ribbon_big_ico_toktokcon.png';
import iconTokTok2x from '../../../img/activeLink/ribbonIcon/ribbon_big_ico_toktokcon@2x.png';
import {
  ITT_FILTER_DATA,
  TOKTOK_GRADE,
  TOKTOK_SCHOOL_CLASS,
  TOKTOK_SEMESTER,
  TOKTOK_SUBJECT
} from '../../../define/activeLinkDefine';
import InputSearch from '../../common/InputSearch';
import ga from '../../../utils/gaConnect';
import useFetchIttContents from '../../../hooks/useFetchIttContents';

const DialogBody = styled.div`
  display: flex;
  flex-flow: column;
  width: 1002px;
  height: 537px;
  ${noTextSelect}
`;

const FilterWrapper = styled.div`
  width: 100%;
  height: 176px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 16px;
  box-sizing: border-box;
`;

const FilterListWrapper = styled.div`
  width: 100%;
  height: 30px;
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const FilterListItem = styled.div`
  border-radius: 15px;
  border: none;
  outline: solid 1px #c9cdd2;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: 5px 16px 6px;
  background-color: #ffffff;
  box-sizing: border-box;
  font-size: 13px;
  text-align: center;
  color: #2f3133;
  ${({ isSubject }) =>
    isSubject &&
    css`
      width: 155px;
    `}

  ${({ isSelect }) =>
    isSelect &&
    css`
      background-color: #daefff;
      font-weight: 700;
      color: #1d7ff9;
      outline: none;
    `}

  ${({ isHide }) =>
    isHide &&
    css`
      display: none;
    `}
`;

const ContentsWrapper = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const SearchWrapper = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ContentsListWrapper = styled.div`
  width: 100%;
  height: 250px;
  padding: 0px 5px;
  margin-top: 10px;
  box-sizing: border-box;
  overflow-y: scroll;
  ${CustomScrollbar}
`;

const FooterWrapper = styled.div`
  width: 100%;
  height: 64px;
  display: flex;
  align-items: center;
  padding: 0px 20px;
  box-sizing: border-box;
`;

const NotSearchMessage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  /* align-items: center; */
  justify-content: center;
  font-size: 14px;
  color: #2f3133;
`;

const ContentsItem = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 230px;
  height: 162px;
  border: solid 1px #c8c7c7;
  cursor: pointer;
  ${noTextSelect}

  ${({ isSelect }) =>
    isSelect &&
    css`
      outline: solid 2px #1d7ff9;
    `}
`;

const ContentsThumbnailWrapper = styled.div`
  /* flex: 1; */
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 130px;
`;

const ContentsThumbnail = styled.img`
  width: 100%;
  height: 100%;
`;
const ContentsName = styled.div`
  text-align: center;
  width: 100%;
  height: 32px;
  padding: 8px 6px;
  box-sizing: border-box;
  font-size: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export default function ActiveLinkInsertITTContents({ isShow }) {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [selectItem, setSelectItem] = useState('');
  const [search, setSearch] = useState('');
  const [isEmpty, setIsEmpty] = useState(true);

  const [query, setQuery] = useState(undefined);
  const [pageNum, setPageNum] = useState(1);
  const { isLoading, error, contentsList, hasMore } = useFetchIttContents(query, pageNum);

  const [subjectFilterOption, setSubjectFilterOption] = useState(TOKTOK_SUBJECT.KOR);
  const [schoolClassFilterOption, setSchoolClassFilterOption] = useState(TOKTOK_SCHOOL_CLASS.ELE);
  const [gradeFilterOption, setGradeFilterOption] = useState(TOKTOK_GRADE.ALL);
  const [semesterFilterOption, setSemesterFilterOption] = useState(TOKTOK_SEMESTER.ALL);
  const poSDKContextState = usePOWebofficeSDKState();
  const notificationPromise = useGenNotificationPromise();

  const contentsRef = useRef();

  const onChangeInput = (e) => {
    setSearch(e.target.value);
  };

  const observer = useRef();
  const lastElementRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPageNum((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore]
  );

  const handleChange = (option) => {
    setQuery(option);
    setPageNum(1);
    if (contentsRef && contentsRef.current) {
      contentsRef.current.scrollTo(0, 0);
    }
  };

  // const onKeyUp = (event) => {
  //   if (event.key === 'Enter') {
  //     onClickSearch();
  //   }
  // };

  const onClickSubjectFilterItem = (item) => {
    if (subjectFilterOption !== item.value) {
      setSubjectFilterOption(item.value);
    }
  };
  const onClickSchoolClassFilterItem = (item, schoolGrade) => {
    if (schoolClassFilterOption !== item.value) {
      setSchoolClassFilterOption(item.value);
      if (item.value !== TOKTOK_SCHOOL_CLASS.ELE && schoolGrade > TOKTOK_GRADE.THIRD) {
        setGradeFilterOption(TOKTOK_GRADE.ALL);
      }
    }
    // if (schoolClassFilterOption.includes(item.value)) {
    //   setSchoolClassFilterOption(schoolClassFilterOption.filter((obj) => obj !== item.value));
    // } else {
    //   setSchoolClassFilterOption([...schoolClassFilterOption, item.value]);
    // }
  };

  const onClickGradeFilterItem = (item) => {
    if (gradeFilterOption !== item.value) {
      setGradeFilterOption(item.value);
    }
    // if (gradeFilterOption.includes(item.value)) {
    //   setGradeFilterOption(gradeFilterOption.filter((obj) => obj !== item.value));
    // } else {
    //   setGradeFilterOption([...gradeFilterOption, item.value]);
    // }
  };
  const onClickSemesterFilterItem = (item) => {
    if (semesterFilterOption !== item.value) {
      setSemesterFilterOption(item.value);
    }
    // if (semesterFilterOption.includes(item.value)) {
    //   setSemesterFilterOption(semesterFilterOption.filter((obj) => obj !== item.value));
    // } else {
    //   setSemesterFilterOption([...semesterFilterOption, item.value]);
    // }
  };

  const getSubFilterList = (list, onClick) => {
    return list.map((item) => (
      <FilterListItem
        key={item.key}
        isSubject
        isSelect={item.value === subjectFilterOption}
        onClick={() => onClick(item)}>
        {t(`ITTContents.Subject.${item.key}`)}
      </FilterListItem>
    ));
  };
  const getClassFilterList = (list, gradeFilterOption, onClick) => {
    return list.map((item) => (
      <FilterListItem
        key={item.key}
        isSelect={item.value === schoolClassFilterOption}
        onClick={() => onClick(item, gradeFilterOption)}>
        {t(`ITTContents.Class.${item.key}`)}
      </FilterListItem>
    ));
  };
  const getGradeFilterList = (list, schoolClass, onClick) => {
    const isElementarySchool = schoolClass === TOKTOK_SCHOOL_CLASS.ELE;
    const isHighGrade = gradeFilterOption > TOKTOK_GRADE.THIRD;
    return list.map((item) => (
      <FilterListItem
        key={item.key}
        isSelect={item.value === gradeFilterOption}
        isHide={!isElementarySchool && item.value > TOKTOK_GRADE.THIRD}
        onClick={() => onClick(item)}>
        {t(`ITTContents.Grade.${item.key}`)}
      </FilterListItem>
    ));
  };
  const getSemesterFilterList = (list, onClick) => {
    return list.map((item) => (
      <FilterListItem
        key={item.key}
        isSelect={item.value === semesterFilterOption}
        onClick={() => onClick(item)}>
        {t(`ITTContents.Semester.${item.key}`)}
      </FilterListItem>
    ));
  };

  const onClickSubmit = () => {
    if (selectItem) {
      ga.event({ category: '액티브링크', action: '콘텐츠추가', label: '톡톡콘텐츠' });
      const contentsUrl = new URL(selectItem.url);
      contentsUrl.protocol = 'https';
      insertActiveLink(poSDKContextState, contentsUrl.href)
        .then(() => {
          dispatch(closeDialog());
        })
        .catch(() => {
          ga.event({ category: '액티브링크', action: '추가실패', label: '톡톡콘텐츠' });
          notificationPromise({
            title: i18n.t('Common.Notification'),
            message: i18n.t('ActiveLink.ITTContentsInsertFail'),
            btn: {
              confirm: {
                text: i18n.t('Common.Confirm')
              }
            },
            showCloseBtn: false
          });
        });
    } else {
      notificationPromise({
        title: i18n.t('Common.Notification'),
        message: i18n.t('ActiveLink.NotSelectedITTContents'),
        btn: {
          confirm: {
            text: i18n.t('Common.Confirm')
          }
        },
        showCloseBtn: false
      });
    }
  };

  const onClickSearch = () => {
    if (!search) {
      setIsEmpty(true);
    } else {
      setIsEmpty(false);
      handleChange({
        searchKeyword: search,
        searchSubject: subjectFilterOption,
        searchSchoolClassCode: schoolClassFilterOption,
        searchGrade: gradeFilterOption,
        searchSemester: semesterFilterOption
      });
    }
  };

  const onClickContentsItem = (item) => {
    if (item === selectItem) {
      setSelectItem(null);
    } else {
      setSelectItem(item);
    }
  };

  return (
    <Dialog
      isShow={isShow}
      showCloseBtn
      onClickCloseBtn={() => dispatch(closeDialog())}
      showTitleSeperateLine
      icon={iconTokTok}
      icon2x={iconTokTok2x}
      title={t('ActiveLink.InsertITTContents')}>
      <DialogBody>
        <FilterWrapper>
          <FilterListWrapper>
            {getSubFilterList(ITT_FILTER_DATA.subject, onClickSubjectFilterItem)}
          </FilterListWrapper>
          <FilterListWrapper>
            {/* <FilterListItem
              key="ALL"
              isSelect={schoolClassFilterOption.length === Object.keys(TOKTOK_SCHOOL_CLASS).length}
              onClick={() => {
                const classes = Object.values(TOKTOK_SCHOOL_CLASS);
                if (schoolClassFilterOption.length === classes.length) {
                  setSchoolClassFilterOption([]);
                } else {
                  setSchoolClassFilterOption(classes);
                }
              }}>
              {t('Common.All')}
            </FilterListItem> */}
            {getClassFilterList(
              ITT_FILTER_DATA.schoolClass,
              gradeFilterOption,
              onClickSchoolClassFilterItem
            )}
          </FilterListWrapper>
          <FilterListWrapper>
            {/* <FilterListItem
              key="ALL"
              isSelect={gradeFilterOption.length === Object.keys(TOKTOK_GRADE).length}
              onClick={() => {
                const grades = Object.values(TOKTOK_GRADE);
                if (gradeFilterOption.length === grades.length) {
                  setGradeFilterOption([]);
                } else {
                  setGradeFilterOption(grades);
                }
              }}>
              {t('Common.All')}
            </FilterListItem> */}
            {getGradeFilterList(
              ITT_FILTER_DATA.grade,
              schoolClassFilterOption,
              onClickGradeFilterItem
            )}
          </FilterListWrapper>
          <FilterListWrapper>
            {/* <FilterListItem
              key="ALL"
              isSelect={semesterFilterOption.length === Object.keys(TOKTOK_SEMESTER).length}
              onClick={() => {
                const semesters = Object.values(TOKTOK_SEMESTER);
                if (semesterFilterOption.length === semesters.length) {
                  setSemesterFilterOption([]);
                } else {
                  setSemesterFilterOption(semesters);
                }
              }}>
              {t('Common.All')}
            </FilterListItem> */}
            {getSemesterFilterList(ITT_FILTER_DATA.semester, onClickSemesterFilterItem)}
          </FilterListWrapper>
        </FilterWrapper>
        <ContentsWrapper>
          <SearchWrapper>
            <InputSearch
              autoFocus={true}
              value={search}
              onChange={onChangeInput}
              onSearch={onClickSearch}
              placeholder={t('Common.InputSearchText')}></InputSearch>
          </SearchWrapper>
          <ContentsListWrapper ref={contentsRef}>
            {isEmpty ? (
              <NotSearchMessage>{t('ActiveLink.EmptySearchWord')}</NotSearchMessage>
            ) : (
              <>
                {contentsList && contentsList.length > 0 ? (
                  <GridLayout
                    column={4}
                    gap={10}
                    cssExt={css`
                      padding: 5px;
                      box-sizing: border-box;
                    `}>
                    {contentsList.map((item, i) => {
                      return (
                        <ContentsItem
                          key={item.contentsId}
                          onClick={() => onClickContentsItem(item)}
                          isSelect={item === selectItem}
                          ref={contentsList.length === i + 1 ? lastElementRef : undefined}>
                          <ContentsThumbnailWrapper>
                            <ContentsThumbnail src={item.contentsThumbUrl} />
                          </ContentsThumbnailWrapper>
                          <ContentsName title={item.contentsNm}>{item.contentsNm}</ContentsName>
                        </ContentsItem>
                      );
                    })}
                  </GridLayout>
                ) : (
                  <NotSearchMessage>{t('ActiveLink.NotSearchResult')}</NotSearchMessage>
                )}
              </>
            )}
          </ContentsListWrapper>
        </ContentsWrapper>
        <FooterWrapper>
          <ActiveLinkDialogFooter onSubmit={onClickSubmit}></ActiveLinkDialogFooter>
        </FooterWrapper>
      </DialogBody>
    </Dialog>
  );
}
